import React, { useState } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import UserAccountMenu from '../../components/ui/UserAccountMenu';
import PressReleaseForm from './components/PressReleaseForm';
import PressReleasePreview from './components/PressReleasePreview';
import PressReleaseActions from './components/PressReleaseActions';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const PressReleaseTemplate = () => {
  const { isAuthenticated } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    surgeonName: '',
    practiceName: '',
    city: '',
    state: '',
    quote: '',
    practiceDescription: '',
    contactName: '',
    contactTitle: '',
    contactEmail: '',
    contactPhone: '',
    website: ''
  });

  const [errors, setErrors] = useState({});

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.surgeonName?.trim()) {
      newErrors.surgeonName = 'Surgeon name is required';
    }

    if (!formData?.practiceName?.trim()) {
      newErrors.practiceName = 'Practice name is required';
    }

    if (!formData?.city?.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData?.state) {
      newErrors.state = 'State is required';
    }

    if (!formData?.quote?.trim()) {
      newErrors.quote = 'Quote is required';
    } else if (formData?.quote?.trim()?.length < 50) {
      newErrors.quote = 'Quote must be at least 50 characters';
    }

    if (!formData?.contactName?.trim()) {
      newErrors.contactName = 'Contact name is required';
    }

    if (!formData?.contactEmail?.trim()) {
      newErrors.contactEmail = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.contactEmail)) {
      newErrors.contactEmail = 'Invalid email format';
    }

    if (!formData?.contactPhone?.trim()) {
      newErrors.contactPhone = 'Contact phone is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <div className="lg:ml-60">
        <header className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
          <div className="flex items-center justify-between h-20 px-4 md:px-6 lg:px-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 0.1)))' }}>
                <Icon name="FileText" size={24} color="black" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-foreground">Press Release Template</h1>
                <p className="text-sm text-muted-foreground font-caption hidden md:block">
                  Generate professional press releases for media distribution
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {isAuthenticated && (
                <Button
                  variant={isEditing ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setIsEditing(!isEditing)}
                  iconName={isEditing ? 'Check' : 'Edit'}
                  iconPosition="left"
                >
                  {isEditing ? 'Done Editing' : 'Edit Mode'}
                </Button>
              )}
              <UserAccountMenu />
            </div>
          </div>
        </header>

        <main className="p-4 md:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto space-y-6">
            <div className="border rounded-lg p-4 md:p-6" style={{ backgroundColor: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 0.05)))', borderColor: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 0.2)))' }}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 0.1)))' }}>
                  <Icon name="Lightbulb" size={20} color="black" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-lg font-semibold text-foreground">Create Your Press Release</h2>
                  <p className="text-sm text-foreground/80 leading-relaxed font-caption">
                    Fill in your practice information to generate a professional press release announcing your adoption of Videra Surgical technology. The template includes industry-standard formatting and compliance-safe language. Review the live preview and export in your preferred format for immediate distribution to media outlets.
                    {isAuthenticated && isEditing && (
                      <span className="block mt-2 text-primary font-medium">Edit mode is active - you can modify all template fields.</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                <PressReleaseForm
                  formData={formData}
                  onChange={handleFormChange}
                  errors={errors}
                  isEditable={!isAuthenticated || isEditing}
                />

                <PressReleaseActions
                  formData={formData}
                  onValidate={validateForm}
                />
              </div>

              <div className="lg:sticky lg:top-24 lg:self-start">
                <PressReleasePreview formData={formData} />
              </div>
            </div>

            <div className="bg-muted/30 border border-border rounded-lg p-6 md:p-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Press Release Best Practices</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Icon name="CheckCircle2" size={20} className="text-success mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Timing</p>
                      <p className="text-sm text-muted-foreground font-caption">Distribute during business hours (Tuesday-Thursday preferred)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Icon name="CheckCircle2" size={20} className="text-success mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Distribution</p>
                      <p className="text-sm text-muted-foreground font-caption">Target local media, industry publications, and online platforms</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Icon name="CheckCircle2" size={20} className="text-success mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Follow-up</p>
                      <p className="text-sm text-muted-foreground font-caption">Contact media outlets 2-3 days after distribution</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Icon name="CheckCircle2" size={20} className="text-success mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Social Media</p>
                      <p className="text-sm text-muted-foreground font-caption">Share on practice social channels with #VideraSurgical</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Icon name="CheckCircle2" size={20} className="text-success mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Website</p>
                      <p className="text-sm text-muted-foreground font-caption">Post to practice news section and blog</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Icon name="CheckCircle2" size={20} className="text-success mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Coordination</p>
                      <p className="text-sm text-muted-foreground font-caption">Notify Videra Surgical for co-marketing support</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <footer className="bg-card border-t border-border mt-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground font-caption">
                &copy; {new Date()?.getFullYear()} Videra Surgical. All rights reserved.
              </p>
              <div className="flex items-center gap-6">
                <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors font-caption">
                  Privacy Policy
                </a>
                <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors font-caption">
                  Terms of Service
                </a>
                <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors font-caption">
                  Support
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default PressReleaseTemplate;